import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import AdFileUpload from '../../../../../../../ReusableFormFields/AdFileUpload'
import RadioBlock from '../../../../../../../../components/Form/RadioBlock'
import GoogleImageFilesLibrary from './GoogleImageFilesLibrary'

import { useMediaUpload } from '../../../../../../../ReusableFormFields/AdFileUpload/hooks/useMediaUpload'

import { fileUpload } from '../../../../../../../../modules/actions/files'
import { selectedGoogleAdAccountSelector } from '../../../../../../../../modules/selectors/adAccounts'

import { GOOGLE_PLATFORM } from '../../../../../../../../constants/selectLists/platformList'

import { ASSET_ID, FILE_NAME, FILE_TYPE, FILE_URL, initialGoogleFile, MEDIA_TYPE } from '../../../../../fields'
import {
  MEDIA_FILE_UPLOAD,
  UPLOADED_MEDIA_TYPE,
  MEDIA_FILE_IMAGE_USE_LIBRARY,
  AD_FILE
} from '../../../../../../../ReusableFormFields/AdFileUpload/fields'

import useStyles from './styles'

function GoogleMediaUpload({ formik, itemPath, fileValidationSchema, onMediaRemove, width, height }) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const selectedAdAccount = useSelector(selectedGoogleAdAccountSelector)

  const { values, setFieldValue } = formik

  const {
    fileNameKey,
    fileURLKey,
    fileTypeKey,
    mediaOptionKey,
    uploadedMediaTypeKey,
    mediaFile,
    selectedMediaOption,
    mediaMissingError,
    mediaTouched,
    mediaError,
    handleMediaRemove
  } = useMediaUpload({ formik, itemPath, onMediaRemove, initialMediaFileValues: initialGoogleFile })

  const handleMediaStartUpload = useCallback(
    (file, newFileValues) => {
      setFieldValue(`${itemPath}[${AD_FILE}]`, newFileValues[AD_FILE])
      setFieldValue(`${itemPath}[${FILE_NAME}]`, newFileValues.file_name)
      setFieldValue(`${itemPath}[${FILE_TYPE}]`, newFileValues[MEDIA_TYPE])
      setFieldValue(`${itemPath}[${MEDIA_TYPE}]`, newFileValues[UPLOADED_MEDIA_TYPE])

      // upload file to server
      dispatch(
        fileUpload(file, {
          isPlatformRelated: true,
          platform: GOOGLE_PLATFORM,
          isPrivate: false,
          account: selectedAdAccount.id
        })
      )
    },
    [dispatch, itemPath, setFieldValue, selectedAdAccount]
  )

  const handleMediaUploaded = useCallback(
    uploadedFile => {
      const assetFileFields = {
        ...mediaFile,
        [FILE_URL]: uploadedFile.image_asset?.full_size?.url,
        [ASSET_ID]: uploadedFile.resource_name
      }
      setFieldValue(itemPath, assetFileFields)
    },
    [itemPath, setFieldValue, mediaFile]
  )

  const handleExistingMediaSelected = useCallback(
    (selectedMedia, mediaType) => {
      const { name, url, resource_name } = selectedMedia
      // remove file when select file from another type
      handleMediaRemove()
      const newFileValues = {
        ...mediaFile,
        [FILE_NAME]: name,
        [FILE_URL]: url,
        [ASSET_ID]: resource_name,
        [UPLOADED_MEDIA_TYPE]: selectedMediaOption,
        [FILE_TYPE]: mediaType
      }

      setFieldValue(itemPath, newFileValues)
    },
    [handleMediaRemove, setFieldValue, selectedMediaOption, mediaFile, itemPath]
  )

  return (
    <div className={classes.fileContainer}>
      <RadioBlock
        setFieldValue={setFieldValue}
        id={`${itemPath}_radio_media_file_upload`}
        name={mediaOptionKey}
        value={MEDIA_FILE_UPLOAD}
        selectedValue={selectedMediaOption}
        label={t('Upload a file from my device')}
      >
        <AdFileUpload
          values={formik.values}
          fileValidationSchema={fileValidationSchema}
          onFileStartUpload={handleMediaStartUpload}
          onFileUploaded={handleMediaUploaded}
          onFileRemove={handleMediaRemove}
          error={mediaMissingError}
          mediaType={MEDIA_FILE_UPLOAD}
          fileKey={`${itemPath}[${AD_FILE}]`}
          fileNameKey={`${itemPath}[${FILE_NAME}]`}
          fileURLKey={fileURLKey}
          uploadedMediaTypeKey={uploadedMediaTypeKey}
          fileTypeKey={fileTypeKey}
          accept="image/png,image/jpg,image/jpeg,image/gif"
          maxSize={5242880}
        />
      </RadioBlock>

      <RadioBlock
        setFieldValue={setFieldValue}
        id={`${itemPath}_radio_media_file_use_library_image`}
        name={mediaOptionKey}
        value={MEDIA_FILE_IMAGE_USE_LIBRARY}
        selectedValue={selectedMediaOption}
        label={t('Use an existing image from the library')}
        touched={mediaTouched}
        error={mediaError}
      >
        <GoogleImageFilesLibrary
          values={values}
          onMediaSelect={handleExistingMediaSelected}
          onFileRemove={handleMediaRemove}
          mediaType={MEDIA_FILE_IMAGE_USE_LIBRARY}
          fileNameKey={fileNameKey}
          fileURLKey={fileURLKey}
          uploadedMediaTypeKey={uploadedMediaTypeKey}
          adAccountId={selectedAdAccount.id}
          width={width}
          height={height}
        />
      </RadioBlock>
    </div>
  )
}

GoogleMediaUpload.propTypes = {
  formik: PropTypes.object.isRequired,
  itemPath: PropTypes.string.isRequired,
  fileValidationSchema: PropTypes.object,
  onMediaRemove: PropTypes.func
}

export default GoogleMediaUpload
